<!--
 * @Description: 安装信息变更弹窗（参考 reportedConsumables.vue 交互骨架）
-->
<template>
  <el-dialog
    v-dialogDrag
    title="安装信息变更"
    :visible.sync="visibleSync"
    width="980px"
    :before-close="onBeforeClose"
    :close-on-click-modal="false"
    v-loading="loading"
    :element-loading-background="loadingBackground"
  >
    <div class="content">
      <el-alert
        title="下方展示了客户下单产品，如存在客户原有摄像头无法适配，需安装我司摄像头的场景，请提前与客户沟通确认。确认无误后，在下方勾选无法利旧的产品,系统将结合上报配件，自动转换为我司对应的产品。"
        type="info"
        :closable="false"
        show-icon
        class="mb-10"
      />

      <!-- 设备图片上传区域：当任一产品勾选"无法利旧"时展示 -->
      <div class="upload-section" v-if="showUploadSection">
        <div class="upload-item">
          <uploadOBS
            class="avatar-uploader"
            :fileType="2"
            :limitSize="10"
            :showFileList="false"
            :showBtnType="0"
            canRemove
            :limitConfig="['jpeg', 'png', 'bmp', 'gif', 'jpg']"
            v-loading="ocrLoading"
            element-loading-text="OCR识别中..."
            element-loading-background="rgba(255,255,255,0.8)"
            @success="handleNameplateSuccess"
          >
            <div slot="preview" class="upload-preview">
              <img
                v-if="nameplateInfo.url"
                :src="nameplateInfo.url"
                class="upload-image"
              />
              <i v-else class="el-icon-plus upload-icon"></i>
            </div>
          </uploadOBS>
          <div class="upload-label"><span class="required">*</span>设备铭牌</div>
        </div>
        <div class="upload-item">
          <uploadOBS
            class="avatar-uploader"
            :fileType="2"
            :limitSize="10"
            :showFileList="false"
            :showBtnType="0"
            canRemove
            :limitConfig="['jpeg', 'png', 'bmp', 'gif', 'jpg']"
            @success="handleFrontPanelSuccess"
          >
            <div slot="preview" class="upload-preview">
              <img
                v-if="frontPanelInfo.url"
                :src="frontPanelInfo.url"
                class="upload-image"
              />
              <i v-else class="el-icon-plus upload-icon"></i>
            </div>
          </uploadOBS>
          <div class="upload-label"><span class="required">*</span>设备前面板</div>
        </div>
        <div class="upload-item">
          <uploadOBS
            class="avatar-uploader"
            :fileType="2"
            :limitSize="10"
            :showFileList="false"
            :showBtnType="0"
            canRemove
            :limitConfig="['jpeg', 'png', 'bmp', 'gif', 'jpg']"
            @success="handleInterfaceSuccess"
          >
            <div slot="preview" class="upload-preview">
              <img
                v-if="interfaceInfo.url"
                :src="interfaceInfo.url"
                class="upload-image"
              />
              <i v-else class="el-icon-plus upload-icon"></i>
            </div>
          </uploadOBS>
          <div class="upload-label"><span class="required">*</span>设备接口</div>
        </div>
        <!-- 制造商和设备型号输入框 -->
        <div class="input-row">
          <div class="input-item">
            <span class="input-label"><span class="required">*</span>制造商：</span>
            <el-input v-model="deviceInfo.manufacturer" placeholder="请输入制造商" size="small"></el-input>
          </div>
          <div class="input-item">
            <span class="input-label"><span class="required">*</span>设备型号：</span>
            <el-input v-model="deviceInfo.model" placeholder="请输入设备型号" size="small"></el-input>
          </div>
        </div>
      </div>

      <el-tabs @tab-click="switchPage" v-model="activeName">
        <el-tab-pane
          :label="item.combinationName"
          v-for="item in combinationList"
          :key="item.combinationId"
          :name="item.combinationId.toString()"
        >
          <install-change-table
            ref="tableComponent"
            :basicInfo="basicInfo"
            :combinationId="item.combinationId"
            :isInstallReport="isInstallReport"
            @selection-change="handleSelectionChange"
          />
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="dialog-footer" slot="footer">
      <el-button class="cancel" @click="emitClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm" v-if="isInstallReport !== 2">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { printError } from "@/utils/util";
import { getCombinationList, reportParts, nameplateIdentifyNew, queryReuseHostPhotos } from "@/api";
import InstallChangeTable from "./installChangeTable.vue";
import uploadOBS from "@/components/uploadOBS/uploadOBS";

export default {
  name: "installChangeDialog",
  props: {
    visible: { type: Boolean, default: false },
    basicInfo: { type: Object, default: () => ({}) },
    isInstallReport: { type: Number, default: null }
  },
  data() {
    return {
      visibleSync: false,
      loading: false,
      loadingBackground: "rgba(255,255,255,0.5)",
      combinationList: [],
      activeName: "",
      // 设备图片上传相关数据
      nameplateInfo: { url: "", fileId: "" }, // 设备铭牌
      frontPanelInfo: { url: "", fileId: "" }, // 设备前面板
      interfaceInfo: { url: "", fileId: "" }, // 设备接口
      deviceInfo: { manufacturer: "", model: "" }, // 制造商和设备型号
      ocrLoading: false, // OCR识别loading
      selectionMap: {} // key: combinationId, value: boolean（该组合下是否有勾选无法利旧）
    };
  },
  components: { InstallChangeTable, uploadOBS },
  computed: {
    // 是否展示设备图片上传区域：任一组合下有勾选"无法利旧"即显示
    showUploadSection() {
      return Object.values(this.selectionMap).some(v => v === true);
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.visibleSync = val;
        if (val) {
          this.initData();
        }
      }
    },
    visibleSync(val) {
      if (!val) this.$emit("update:visible", false);
    }
  },
  methods: {
    async initData() {
      try {
        this.loading = true;
        this.selectionMap = {}; // 重置勾选状态映射
        // 先获取组合列表
        const resComb = await getCombinationList({ id: this.basicInfo.itemId });
        this.combinationList = resComb.data || [];
        this.activeName = this.combinationList.length ? this.combinationList[0].combinationId.toString() : "";

        // 回显利旧主机照片和设备信息
        if (this.activeName) {
          await this.loadHostPhotos(this.activeName);
        }
      } catch (error) {
        printError(error);
      } finally {
        this.loading = false;
      }
    },
    // 加载利旧主机照片和设备信息（回显）
    async loadHostPhotos(combinationId) {
      try {
        const res = await queryReuseHostPhotos({ combinationId: Number(combinationId) });
        if (res.code === "OK" && res.data) {
          const data = res.data;
          // 回显设备铭牌
          if (data.nameplateFileId || data.nameplateFileUrl) {
            this.nameplateInfo = {
              fileId: data.nameplateFileId || "",
              url: data.nameplateFileUrl || ""
            };
          }
          // 回显设备前面板
          if (data.frontPanelFileId || data.frontPanelFileUrl) {
            this.frontPanelInfo = {
              fileId: data.frontPanelFileId || "",
              url: data.frontPanelFileUrl || ""
            };
          }
          // 回显设备接口
          if (data.interfaceFileId || data.interfaceFileUrl) {
            this.interfaceInfo = {
              fileId: data.interfaceFileId || "",
              url: data.interfaceFileUrl || ""
            };
          }
          // 回显制造商和设备型号
          this.deviceInfo = {
            manufacturer: data.reuseManufacturer || "",
            model: data.reuseDeviceModel || ""
          };
        }
      } catch (error) {
        console.error("回显利旧主机信息失败:", error);
      }
    },
    switchPage(tab) {
      this.activeName = tab.name;
      // 仅当上传区域可见时回显利旧主机照片
      if (this.showUploadSection) {
        this.loadHostPhotos(tab.name);
      }
    },
    onBeforeClose() {
      this.emitClose();
    },
    // 处理子组件勾选状态变化
    handleSelectionChange({ combinationId, hasSelected }) {
      this.$set(this.selectionMap, combinationId, hasSelected);
    },
    emitClose() {
      this.$emit("closeDialog", "installChangeVisible");
      this.visibleSync = false;
    },
    // 设备铭牌上传成功处理
    async handleNameplateSuccess(res) {
      if (res.code === "OK" && res.data) {
        const uploadedFile = res.data[0];
        this.nameplateInfo = {
          url: uploadedFile.allFileUrl,
          fileId: uploadedFile.fileId
        };
        this.$message.success("设备铭牌上传成功");

        // 调用OCR接口识别制造商和设备型号
        try {
          this.ocrLoading = true;
          const ocrRes = await nameplateIdentifyNew({
            imageUrl: uploadedFile.allFileUrl,
            fileType: 3
          });
          if (ocrRes.code === "OK" && ocrRes.data) {
            const { manufacturer, model } = ocrRes.data;
            // 自动填充制造商
            if (manufacturer) {
              this.deviceInfo.manufacturer = manufacturer;
            }
            // 自动填充设备型号
            if (model) {
              this.deviceInfo.model = model;
            }
            this.$message.success("OCR识别成功，已自动填充制造商和设备型号");
          }
        } catch (error) {
          console.error("OCR识别失败:", error);
          this.$message.warning("OCR识别失败，请手动输入制造商和设备型号");
        } finally {
          this.ocrLoading = false;
        }
      } else {
        this.$message.error(res.message || "设备铭牌上传失败");
      }
    },
    // 设备前面板上传成功处理
    handleFrontPanelSuccess(res) {
      if (res.code === "OK" && res.data) {
        const uploadedFile = res.data[0];
        this.frontPanelInfo = {
          url: uploadedFile.allFileUrl,
          fileId: uploadedFile.fileId
        };
        this.$message.success("设备前面板上传成功");
      } else {
        this.$message.error(res.message || "设备前面板上传失败");
      }
    },
    // 设备接口上传成功处理
    handleInterfaceSuccess(res) {
      if (res.code === "OK" && res.data) {
        const uploadedFile = res.data[0];
        this.interfaceInfo = {
          url: uploadedFile.allFileUrl,
          fileId: uploadedFile.fileId
        };
        this.$message.success("设备接口上传成功");
      } else {
        this.$message.error(res.message || "设备接口上传失败");
      }
    },
    async handleConfirm() {
      try {
        this.loading = true;

        // 校验必填字段（仅当上传区域可见时校验）
        if (this.showUploadSection) {
          if (!this.nameplateInfo.url) {
            this.$message.warning("请上传设备铭牌");
            return;
          }
          if (!this.frontPanelInfo.url) {
            this.$message.warning("请上传设备前面板");
            return;
          }
          if (!this.interfaceInfo.url) {
            this.$message.warning("请上传设备接口");
            return;
          }
          if (!this.deviceInfo.manufacturer) {
            this.$message.warning("请输入制造商");
            return;
          }
          if (!this.deviceInfo.model) {
            this.$message.warning("请输入设备型号");
            return;
          }
        }

        // 获取所有表格组件的数据
        const tableComponents = this.$refs.tableComponent || [];
        const combinationList = [];
        
        // 遍历所有组合（标签页）
        for (const combination of this.combinationList) {
          // 找到对应的表格组件
          const tableComponent = tableComponents.find(
            comp => comp.combinationId === combination.combinationId
          );
          
          const details = [];
          
          if (tableComponent && tableComponent.selectedTableData) {
            // 处理每个选中的产品
            for (const row of tableComponent.selectedTableData) {
              if (row.isNotUseOldSelected) {
                const materials = [];
                
                // 获取该产品的物料数据
                const materialData = tableComponent.materialTableData.find(
                  item => item.oldInstallDetailId === row.oldInstallDetailId
                );
                
                if (materialData && materialData.materials) {
                  // 处理每个物料
                  for (const material of materialData.materials) {
                    if (material.amount > 0) {
                      materials.push({
                        amount: material.amount,
                        materialName: material.materialName,
                        materialNo: material.materialNo,
                        maxReportAmount: material.maxReportAmount,
                        productCode: material.productCode,
                        productName: material.productName,
                        stockAmount: material.stockAmount
                      });
                    }
                  }
                }
                
                // 获取文件ID列表（从凭证文件中提取fileId）
                const proofFiles = tableComponent.getProofFiles(row);
                const fileIdList = proofFiles.map(file => file.fileId || file.id).filter(id => id);
                
                // 获取无法利旧原因列表（从row.notUseReason中获取）
                const isNotUseOldReasonList = Array.isArray(row.notUseReason) ? row.notUseReason : [];
                
                details.push({
                  oldInstallDetailId: row.oldInstallDetailId,
                  fileIdList: fileIdList,
                  isNotUseOldReasonList: isNotUseOldReasonList,
                  materials: materials,
                  product: {
                    ...materialData.product,
                    installDetailId : materialData.oldInstallDetailId,
                    isReceive : materialData.product.isReceive ? 1:0,
                    unReceiveReason : materialData.product.unReceiveReason
                  },
                });
              }
            }
          }
          
          // 无论是否有勾选"无法利旧"的产品，都需要包含该combinationId
          combinationList.push({
            combinationId: combination.combinationId,
            details: details,
            // 仅当上传区域可见时传递利旧主机照片和设备信息
            ...(this.showUploadSection ? {
              nameplateFileId: this.nameplateInfo.fileId,
              nameplateFileUrl: this.nameplateInfo.url,
              frontPanelFileId: this.frontPanelInfo.fileId,
              frontPanelFileUrl: this.frontPanelInfo.url,
              interfaceFileId: this.interfaceInfo.fileId,
              interfaceFileUrl: this.interfaceInfo.url,
              reuseDeviceModel: this.deviceInfo.model,
              reuseManufacturer: this.deviceInfo.manufacturer
            } : {})
          });
        }
        
        // 构建请求数据
        const payload = {
          combinationList: combinationList
        };
        
        // 调用接口提交数据
        const res = await reportParts(payload);
        if (res.code === "OK") {
          this.$message.success("上报配件成功");
          this.emitClose();
          this.$emit("refresh");
        } else {
          this.$message.error(res.message || "上报配件失败");
        }
      } catch (error) {
        printError(error);
        this.$message.error("上报配件失败");
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/deep/.el-dialog__body {
  padding: 10px;
}
.content {
  margin-top: 10px;
}
.mb-10 {
  margin-bottom: 10px;
}
// 设备图片上传区域样式
.upload-section {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;

  .upload-item {
    display: flex;
    flex-direction: column;
    align-items: center;

    .avatar-uploader {
      ::v-deep .el-upload {
        border: 1px dashed #d9d9d9;
        border-radius: 6px;
        cursor: pointer;
        position: relative;
        overflow: hidden;
        transition: border-color 0.2s;

        &:hover {
          border-color: #409eff;
        }
      }

      .upload-preview {
        width: 80px;
        height: 80px;
        display: flex;
        align-items: center;
        justify-content: center;

        .upload-image {
          width: 80px;
          height: 80px;
          object-fit: cover;
        }

        .upload-icon {
          font-size: 28px;
          color: #8c939d;
        }
      }
    }

    .upload-label {
      margin-top: 8px;
      font-size: 14px;
      color: #606266;

      /deep/ .required {
        color: #f56c6c;
        margin-right: 2px;
      }
    }
  }

  .input-row {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding-left: 20px;
    border-left: 1px solid #dcdfe6;

    .input-item {
      display: flex;
      align-items: center;
      white-space: nowrap;

      .input-label {
        font-size: 14px;
        color: #606266;
        margin-right: 8px;
        min-width: 70px;
        text-align: right;

        /deep/ .required {
          color: #f56c6c;
          margin-right: 2px;
        }
      }

      .el-input {
        width: 140px;
      }
    }
  }
}
</style>


